import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaChartLine } from 'react-icons/fa';
import { Account, AccountFormData, Classification, CategoryType, BankAccountType } from '../../types/accounting';

const CLASSIFICATIONS = [
  { value: 'asset', label: 'Asset', range: '1-xxxx', color: 'badge-success', description: 'Resources owned by the business' },
  { value: 'liability', label: 'Liability', range: '2-xxxx', color: 'badge-error', description: 'Money owed by the business' },
  { value: 'equity', label: 'Equity', range: '3-xxxx', color: 'badge-info', description: 'Owner\'s stake in the business' },
  { value: 'income', label: 'Income', range: '4-xxxx', color: 'badge-accent', description: 'Revenue from sales and services' },
  { value: 'cost_of_sales', label: 'Cost of Sales', range: '5-xxxx', color: 'badge-warning', description: 'Direct costs of goods sold' },
  { value: 'expense', label: 'Expense', range: '6-xxxx', color: 'badge-neutral', description: 'Operating expenses' },
  { value: 'other_income', label: 'Other Income', range: '8-xxxx', color: 'badge-primary', description: 'Non-operating income' },
  { value: 'other_expense', label: 'Other Expense', range: '9-xxxx', color: 'badge-secondary', description: 'Non-operating expenses' },
];

const CATEGORY_TYPES: Record<string, { label: string; description: string }[]> = {
  asset: [
    { label: 'Bank', description: 'Money in the bank, e.g., cheque or savings account' },
    { label: 'Accounts Receivable', description: 'Money owed to you by your customers' },
    { label: 'Other Current Asset', description: 'Assets that can be turned into cash within a year' },
    { label: 'Fixed Asset', description: 'Assets with long life, e.g., buildings, cars, computers' },
    { label: 'Other Asset', description: 'Other assets like loans made to others and goodwill' },
  ],
  liability: [
    { label: 'Credit Card', description: 'Repayments required to service credit card debt' },
    { label: 'Accounts Payable', description: 'Money owed by you to your suppliers' },
    { label: 'Other Current Liability', description: 'Money owed that is due in less than a year, e.g., GST' },
    { label: 'Long Term Liability', description: 'Money owed that is due in more than one year, e.g., business loan' },
    { label: 'Other Liability', description: 'Other money you owe' },
  ],
  equity: [
    { label: 'Equity', description: 'Net worth: assets minus liabilities. E.g., retained earnings, shareholders\' equity' },
  ],
  income: [
    { label: 'Income', description: 'Revenue from the sale of goods and services' },
  ],
  cost_of_sales: [
    { label: 'Cost of Sales', description: 'Direct cost of selling goods and providing services' },
  ],
  expense: [
    { label: 'Expense', description: 'Day-to-day expenses of running your business' },
  ],
  other_income: [
    { label: 'Other Income', description: 'Other revenues, e.g., interest earned on savings' },
  ],
  other_expense: [
    { label: 'Other Expense', description: 'Other expenses, e.g., interest charged' },
  ],
};

const BANK_ACCOUNT_TYPES = [
  { value: 'savings', label: 'Savings Account', description: 'For setting money aside, earns higher interest' },
  { value: 'everyday', label: 'Everyday Account', description: 'Main account for day-to-day transactions' },
  { value: 'overdraft', label: 'Overdraft Account', description: 'Allows overdrawing by agreed amount' },
  { value: 'foreign', label: 'Foreign Account', description: 'Based outside Australia or uses foreign currency' },
  { value: 'third_party', label: 'Third Party Account', description: 'Managed by another party, like PayPal, Shopify' },
  { value: 'clearing', label: 'Clearing Account', description: 'Temporarily holds transactions until allocated' },
  { value: 'cash_on_hand', label: 'Cash on Hand', description: 'Cash available for immediate use, like petty cash' },
];

const ChartOfAccounts: React.FC = () => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    classification: 'asset' as Account['classification'],
    category_type: '',
    bank_account_type: '',
    parent_account: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadAccounts(1);
  }, [token]);

  const loadAccounts = async (page: number = 1) => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await apiCall(
        `${ENDPOINTS.ACCOUNTING.CHART_OF_ACCOUNTS}?page=${page}`,
        { method: 'GET' },
        token || undefined
      );
      const data = await response.json();
      setAccounts(data.results || data || []);
      setTotalCount(data.count || 0);
      setCurrentPage(page);
      // Calculate total pages (PAGE_SIZE is 10 in backend)
      setTotalPages(Math.ceil((data.count || 0) / 10));
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!token) return;
    
    if (!formData.code || !formData.name) {
      alert('Please fill in required fields (Code and Name)');
      return;
    }

    try {
      const url = editingAccount 
        ? `${ENDPOINTS.ACCOUNTING.CHART_OF_ACCOUNTS}${editingAccount.id}/`
        : ENDPOINTS.ACCOUNTING.CHART_OF_ACCOUNTS;
      
      const method = editingAccount ? 'PATCH' : 'POST';
      
      const response = await apiCall(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        },
        token || undefined
      );

      if (response.ok) {
        await loadAccounts(currentPage);
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving account:', error);
      alert('Failed to save account');
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to delete this account?')) return;

    try {
      const response = await apiCall(
        `${ENDPOINTS.ACCOUNTING.CHART_OF_ACCOUNTS}${id}/`,
        { method: 'DELETE' },
        token || undefined
      );

      if (response.ok) {
        await loadAccounts(currentPage);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account');
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setFormData({
      code: account.code,
      name: account.name,
      classification: account.classification,
      category_type: account.category_type,
      bank_account_type: account.bank_account_type || '',
      parent_account: account.parent_account || '',
      description: account.description,
      is_active: account.is_active,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    setFormData({
      code: '',
      name: '',
      classification: 'asset',
      category_type: '',
      bank_account_type: '',
      parent_account: '',
      description: '',
      is_active: true,
    });
  };

  const filteredAccounts = accounts
    .filter(account => {
      const matchesSearch = 
        account.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.category_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || account.classification === filterType;
      const matchesActive = showInactive || account.is_active;
      return matchesSearch && matchesType && matchesActive;
    })
    .sort((a, b) => a.code.localeCompare(b.code));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <p className="mt-4 text-lg">Loading Chart of Accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <FaChartLine className="text-primary" />
              Chart of Accounts
            </h1>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary gap-2"
          >
            <FaPlus /> Add Account
          </button>
        </div>

        {/* Filters */}
        <div className="bg-base-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm font-medium whitespace-nowrap">Search</span>
              <input
                type="text"
                placeholder="Search by code or name..."
                className="input input-bordered w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Classification</span>
              <select
                className="select select-bordered"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Classifications</option>
                {CLASSIFICATIONS.map(cls => (
                  <option key={cls.value} value={cls.value}>
                    {cls.label} ({cls.range})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium whitespace-nowrap">Show Inactive</span>
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      <div className="bg-base-100 rounded-lg shadow overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="card-title text-xl">
              All Accounts ({filteredAccounts.length})
            </h2>
          </div>

          {filteredAccounts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account Name</th>
                    <th>Classification</th>
                    <th>Category Type</th>
                    <th>Parent</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map(account => {
                    const classification = CLASSIFICATIONS.find(c => c.value === account.classification);
                    return (
                      <tr key={account.id} className={!account.is_active ? 'opacity-50' : ''}>
                        <td className="font-mono font-bold">{account.code}</td>
                        <td>
                          <div className="font-semibold">{account.name}</div>
                          {account.description && (
                            <div className="text-xs opacity-60">{account.description}</div>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${classification?.color || 'badge-ghost'} badge-sm`}>
                            {classification?.label || account.classification}
                          </span>
                        </td>
                        <td className="text-sm">{account.category_type || '-'}</td>
                        <td className="text-sm opacity-60">
                          {account.parent_account_name || '-'}
                        </td>
                        <td className="font-semibold">{formatCurrency(account.balance)}</td>
                        <td>
                          <span className={`badge ${account.is_active ? 'badge-success' : 'badge-ghost'} badge-sm`}>
                            {account.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(account)}
                              className="btn btn-sm btn-ghost text-primary"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(account.id)}
                              className="btn btn-sm btn-ghost text-error"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 opacity-60">
              <p>No accounts found</p>
            </div>
          )}

          {/* Pagination */}
          {!loading && filteredAccounts.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm opacity-60">
                Showing {filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''} (Page {currentPage} of {totalPages}, Total: {totalCount})
              </div>
              <div className="join">
                <button 
                  className="join-item btn btn-sm" 
                  onClick={() => loadAccounts(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button className="join-item btn btn-sm btn-active">{currentPage}</button>
                <button 
                  className="join-item btn btn-sm"
                  onClick={() => loadAccounts(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg mb-4">
              {editingAccount ? 'Edit Account' : 'Create New Account'}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Account Code *</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 1000"
                    className="input input-bordered"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Classification *</span>
                  </label>
                  <select
                    className="select select-bordered"
                    value={formData.classification}
                    onChange={(e) => setFormData({ ...formData, classification: e.target.value as Account['classification'] })}
                  >
                    {CLASSIFICATIONS.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Account Name *</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Cash at Bank"
                  className="input input-bordered"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Parent Account</span>
                </label>
                <select
                  className="select select-bordered"
                  value={formData.parent_account}
                  onChange={(e) => setFormData({ ...formData, parent_account: e.target.value })}
                >
                  <option value="">None (Top Level)</option>
                  {accounts
                    .filter(acc => acc.classification === formData.classification && acc.id !== editingAccount?.id)
                    .map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.code} - {acc.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Description</span>
                </label>
                <textarea
                  className="textarea textarea-bordered"
                  placeholder="Brief description of this account..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text font-semibold">Active</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                </label>
              </div>
            </div>

            <div className="modal-action">
              <button onClick={handleCloseModal} className="btn btn-ghost">
                Cancel
              </button>
              <button onClick={handleCreateOrUpdate} className="btn btn-primary">
                {editingAccount ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccounts;
